(function() {
  'use strict';

  // Cart Drawer API
  const CartDrawer = {
    drawer: null,
    inner: null,
    body: null,
    overlay: null,
    closeBtn: null,
    itemsContainer: null,
    emptyState: null,
    footer: null,
    loadingState: null,
    totalElement: null,
    noteInput: null,
    discountInput: null,
    discountBtn: null,
    discountMessage: null,
    recommendationsContainer: null,
    recommendationsHasContent: false,
    recommendationsVisible: false,

    init: function() {
      // 查找购物车组件，优先查找 header-group 中的组件，如果没有则查找旧的 ID
      this.drawer = document.querySelector('.mt-cart-drawer[data-cart-drawer-id]') || 
                    document.getElementById('MtCartDrawer') ||
                    document.querySelector('.mt-cart-drawer');
      
      if (!this.drawer) return;

      this.inner = this.drawer.querySelector('[data-cart-drawer-inner]');
      this.body = this.drawer.querySelector('[data-cart-drawer-body]');
      this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
      this.itemsContainer = this.drawer.querySelector('[data-cart-drawer-items]');
      this.emptyState = this.drawer.querySelector('[data-cart-drawer-empty]');
      this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
      this.loadingState = this.drawer.querySelector('[data-cart-drawer-loading]');
      this.totalElement = this.drawer.querySelector('[data-cart-drawer-total]');
      // 查找 cart-note，可能带有 section.id 后缀
      this.noteInput = this.drawer.querySelector('[id^="cart-note"]') || 
                       this.drawer.querySelector('#cart-note');
      // 查找优惠码相关元素
      this.discountInput = this.drawer.querySelector('[data-discount-input]');
      this.discountBtn = this.drawer.querySelector('[data-discount-apply]');
      this.discountMessage = this.drawer.querySelector('[data-discount-message]');
      this.recommendationsContainer = this.drawer.querySelector('[data-cart-drawer-recommendations]');
      if (this.recommendationsContainer) {
        this.recommendationsHasContent = this.recommendationsContainer.dataset.hasRecommendations === 'true';
        this.recommendationsVisible = this.recommendationsContainer.dataset.recommendationsVisible === 'true' && this.recommendationsHasContent;
      }

      this.bindEvents();
      // 不在 init 时立即请求 /cart.js，等到用户真正打开抽屉时再加载
      // 避免页面加载阶段 Shopify /cart.js 未就绪导致的错误
    },

    bindEvents: function() {
      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.close.bind(this));
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      // ESC key
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && CartDrawer.isOpen()) {
          CartDrawer.close();
        }
      });

      // Note input change
      if (this.noteInput) {
        let noteTimeout;
        this.noteInput.addEventListener('input', function() {
          clearTimeout(noteTimeout);
          noteTimeout = setTimeout(function() {
            CartDrawer.updateCartNote(this.value);
          }.bind(this), 500);
        });
      }

      // Discount code apply button
      if (this.discountBtn) {
        this.discountBtn.addEventListener('click', this.applyDiscountCode.bind(this));
      }

      // Discount code input Enter key
      if (this.discountInput) {
        this.discountInput.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            CartDrawer.applyDiscountCode();
          }
        });
      }

      // Recommendation add-to-cart buttons
      if (this.drawer) {
        this.drawer.addEventListener('click', function(event) {
          const target = event.target.closest ? event.target.closest('[data-recommendation-atc]') : null;
          if (!target) return;
          event.preventDefault();
          const variantId = target.getAttribute('data-variant-id');
          const quantity = parseInt(target.getAttribute('data-quantity'), 10) || 1;
          if (variantId && typeof window.addToCartAndOpenDrawer === 'function') {
            window.addToCartAndOpenDrawer(variantId, quantity);
          }
        });
      }

      // Listen for cart updates - 只在抽屉打开时才刷新
      document.addEventListener('cart:updated', function() {
        if (CartDrawer.isOpen()) CartDrawer.loadCart();
      });
      if (typeof window.PUB_SUB_EVENTS !== 'undefined') {
        document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, function() {
          if (CartDrawer.isOpen()) CartDrawer.loadCart();
        });
      }
    },

    open: function() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.loadCart();
    },

    close: function() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    isOpen: function() {
      return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
    },

    toggle: function() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    },

    loadCart: function(retryCount) {
      if (!this.drawer) return Promise.reject(new Error('Drawer not found'));

      retryCount = retryCount || 0;
      this.showLoading();

      return fetch('/cart.js')
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to load cart');
          return response.json();
        })
        .then(function(cart) {
          CartDrawer.renderCart(cart);
          CartDrawer.hideLoading();
          return cart;
        })
        .catch(function(error) {
          console.error('Error loading cart:', error);
          // 自动重试一次（处理短暂的网络抖动）
          if (retryCount < 1) {
            return new Promise(function(resolve) {
              setTimeout(function() {
                resolve(CartDrawer.loadCart(retryCount + 1));
              }, 800);
            });
          }
          CartDrawer.hideLoading();
          CartDrawer.showError('Failed to load cart. Please try again.');
          throw error;
        });
    },

    renderCart: function(cart) {
      if (!this.itemsContainer || !this.emptyState || !this.footer) return;

      this.toggleRecommendations(cart.item_count > 0);

      if (cart.item_count === 0) {
        this.itemsContainer.innerHTML = '';
        this.emptyState.style.display = 'flex';
        this.footer.style.display = 'none';
      } else {
        this.emptyState.style.display = 'none';
        this.footer.style.display = 'block';

        // Render items
        let itemsHTML = '';
        cart.items.forEach(function(item) {
          itemsHTML += CartDrawer.renderCartItem(item);
        });
        this.itemsContainer.innerHTML = itemsHTML;

        // Update total
        if (this.totalElement) {
          this.totalElement.textContent = this.formatMoney(cart.total_price);
        }

        // Bind item events
        this.bindItemEvents();

        // Load cart note
        if (this.noteInput && cart.note) {
          this.noteInput.value = cart.note;
        }
      }

      // Update cart count in header
      if (typeof window.updateMegaHeaderCartCount === 'function') {
        window.updateMegaHeaderCartCount();
      }
    },

    renderCartItem: function(item) {
      const image = item.image ? 
        `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" loading="lazy">` :
        '<div class="mt-cart-drawer__item-image-placeholder">No Image</div>';

      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ?
        `<p class="mt-cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : '';

      return `
        <div class="mt-cart-drawer__item" data-cart-item-key="${this.escapeHtml(item.key)}">
          <div class="mt-cart-drawer__item-image">
            <a href="${this.escapeHtml(item.url)}">${image}</a>
          </div>
          <div class="mt-cart-drawer__item-content">
            <div class="mt-cart-drawer__item-header">
              <h3 class="mt-cart-drawer__item-title">
                <a href="${this.escapeHtml(item.url)}">${this.escapeHtml(item.product_title)}</a>
              </h3>
              <button type="button" class="mt-cart-drawer__item-remove" data-cart-item-remove="${this.escapeHtml(item.key)}" aria-label="Remove item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            ${variantTitle}
            <div class="mt-cart-drawer__item-footer">
              <div class="mt-cart-drawer__quantity">
                <button type="button" class="mt-cart-drawer__quantity-btn" data-cart-item-decrease="${this.escapeHtml(item.key)}" aria-label="Decrease quantity">-</button>
                <input type="number" class="mt-cart-drawer__quantity-input" value="${item.quantity}" min="1" data-cart-item-quantity="${this.escapeHtml(item.key)}" aria-label="Quantity">
                <button type="button" class="mt-cart-drawer__quantity-btn" data-cart-item-increase="${this.escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
              </div>
              <span class="mt-cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>
      `;
    },

    bindItemEvents: function() {
      if (!this.itemsContainer) return;

      // Remove buttons
      const removeButtons = this.itemsContainer.querySelectorAll('[data-cart-item-remove]');
      removeButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          const key = btn.getAttribute('data-cart-item-remove');
          CartDrawer.removeItem(key);
        });
      });

      // Quantity buttons
      const decreaseButtons = this.itemsContainer.querySelectorAll('[data-cart-item-decrease]');
      decreaseButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          const key = btn.getAttribute('data-cart-item-decrease');
          const input = CartDrawer.itemsContainer.querySelector(`[data-cart-item-quantity="${key}"]`);
          if (input) {
            const newQuantity = Math.max(1, parseInt(input.value) - 1);
            CartDrawer.updateQuantity(key, newQuantity);
          }
        });
      });

      const increaseButtons = this.itemsContainer.querySelectorAll('[data-cart-item-increase]');
      increaseButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          const key = btn.getAttribute('data-cart-item-increase');
          const input = CartDrawer.itemsContainer.querySelector(`[data-cart-item-quantity="${key}"]`);
          if (input) {
            const newQuantity = parseInt(input.value) + 1;
            CartDrawer.updateQuantity(key, newQuantity);
          }
        });
      });

      // Quantity inputs
      const quantityInputs = this.itemsContainer.querySelectorAll('[data-cart-item-quantity]');
      quantityInputs.forEach(function(input) {
        input.addEventListener('change', function() {
          const key = input.getAttribute('data-cart-item-quantity');
          const quantity = Math.max(1, parseInt(input.value) || 1);
          CartDrawer.updateQuantity(key, quantity);
        });
      });
    },

    updateQuantity: function(key, quantity) {
      if (!key || quantity < 1) return;

      this.showLoading();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      })
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to update quantity');
          return response.json();
        })
        .then(function(cart) {
          CartDrawer.renderCart(cart);
          CartDrawer.hideLoading();
          
          // Dispatch event
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(function(error) {
          console.error('Error updating quantity:', error);
          CartDrawer.hideLoading();
          CartDrawer.loadCart(); // Reload to sync
        });
    },

    removeItem: function(key) {
      if (!key) return;

      this.showLoading();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      })
        .then(function(response) {
          if (!response.ok) throw new Error('Failed to remove item');
          return response.json();
        })
        .then(function(cart) {
          CartDrawer.renderCart(cart);
          CartDrawer.hideLoading();
          
          // Dispatch event
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(function(error) {
          console.error('Error removing item:', error);
          CartDrawer.hideLoading();
          CartDrawer.loadCart(); // Reload to sync
        });
    },

    updateCartNote: function(note) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: note
        })
      })
        .catch(function(error) {
          console.error('Error updating cart note:', error);
        });
    },

    applyDiscountCode: function() {
      if (!this.discountInput || !this.discountBtn) return;

      const code = this.discountInput.value.trim();
      if (!code) {
        this.showDiscountMessage('Please enter a discount code', 'error');
        return;
      }

      // Store original button text
      const originalText = this.discountBtn.textContent;
      if (!this.discountBtn.hasAttribute('data-original-text')) {
        this.discountBtn.setAttribute('data-original-text', originalText);
      }

      // Disable button during request
      this.discountBtn.disabled = true;
      this.discountBtn.textContent = 'Applying...';

      // Hide previous message
      if (this.discountMessage) {
        this.discountMessage.style.display = 'none';
      }

      const self = this;

      // Step 1: Apply discount code via Shopify's discount endpoint
      // This sets a cookie that Shopify uses to apply the discount
      fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(function(response) {
          // If response is ok, discount code is valid
          if (response.ok || response.status === 200 || response.status === 302) {
            // Step 2: Wait a bit for cookie to be set, then reload cart
            return new Promise(function(resolve) {
              setTimeout(function() {
                resolve();
              }, 300);
            });
          } else {
            // Try to get error message
            return response.text().then(function(text) {
              throw new Error(text || 'Invalid discount code');
            });
          }
        })
        .then(function() {
          // Step 3: Reload cart to get updated prices with discount
          return CartDrawer.loadCart();
        })
        .then(function() {
          // Step 4: Check if discount was applied by fetching cart data
          return fetch('/cart.js', {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            },
            credentials: 'same-origin'
          });
        })
        .then(function(response) {
          return response.json();
        })
        .then(function(cart) {
          // Check if we have discount information
          // Shopify stores original total in original_total_price if discount is applied
          const originalTotal = cart.original_total_price || cart.total_price;
          const currentTotal = cart.total_price;
          const hasDiscount = originalTotal > currentTotal;

          if (hasDiscount) {
            const discountAmount = originalTotal - currentTotal;
            const discountText = CartDrawer.formatMoney(discountAmount);
            CartDrawer.showDiscountMessage('Discount code applied! You saved ' + discountText, 'success');
            
            // Update total display immediately
            if (CartDrawer.totalElement) {
              CartDrawer.totalElement.textContent = CartDrawer.formatMoney(currentTotal);
            }
          } else {
            // Discount might need a moment to apply, try one more reload
            setTimeout(function() {
              CartDrawer.loadCart().then(function() {
                fetch('/cart.js', {
                  credentials: 'same-origin'
                })
                  .then(function(res) { return res.json(); })
                  .then(function(updatedCart) {
                    const updatedOriginal = updatedCart.original_total_price || updatedCart.total_price;
                    const updatedCurrent = updatedCart.total_price;
                    if (updatedOriginal > updatedCurrent) {
                      const saved = CartDrawer.formatMoney(updatedOriginal - updatedCurrent);
                      CartDrawer.showDiscountMessage('Discount code applied! You saved ' + saved, 'success');
                      if (CartDrawer.totalElement) {
                        CartDrawer.totalElement.textContent = CartDrawer.formatMoney(updatedCurrent);
                      }
                    } else {
                      // Discount will be applied at checkout
                      CartDrawer.showDiscountMessage('Discount code will be applied at checkout', 'success');
                    }
                    
                    // Re-enable button after delayed check
                    if (self.discountBtn) {
                      self.discountBtn.disabled = false;
                      self.discountBtn.textContent = originalText;
                    }
                  })
                  .catch(function(err) {
                    console.error('Error checking discount after delay:', err);
                    // Re-enable button even on error
                    if (self.discountBtn) {
                      self.discountBtn.disabled = false;
                      self.discountBtn.textContent = originalText;
                    }
                  });
              });
            }, 500);
            
            // Clear input for delayed case
            if (self.discountInput) {
              self.discountInput.value = '';
            }
            
            // Return early to avoid re-enabling button immediately
            return;
          }

          // Clear input
          if (self.discountInput) {
            self.discountInput.value = '';
          }
          
          // Re-enable button
          if (self.discountBtn) {
            self.discountBtn.disabled = false;
            self.discountBtn.textContent = originalText;
          }
        })
        .catch(function(error) {
          console.error('Error applying discount code:', error);
          
          // Show error message
          let errorMsg = 'Invalid or expired discount code. Please try again.';
          if (error.message && error.message.includes('Invalid')) {
            errorMsg = 'Invalid discount code. Please check and try again.';
          }
          
          CartDrawer.showDiscountMessage(errorMsg, 'error');
          
          // Re-enable button
          if (self.discountBtn) {
            self.discountBtn.disabled = false;
            self.discountBtn.textContent = originalText;
          }
        });
    },

    showDiscountMessage: function(message, type) {
      if (!this.discountMessage) return;

      this.discountMessage.textContent = message;
      this.discountMessage.className = 'mt-cart-drawer__discount-message';
      this.discountMessage.classList.add('mt-cart-drawer__discount-message--' + (type || 'error'));
      this.discountMessage.style.display = 'block';

      // Auto-hide success messages after 3 seconds
      // if (type === 'success') {
      //   setTimeout(function() {
      //     if (CartDrawer.discountMessage) {
      //       CartDrawer.discountMessage.style.display = 'none';
      //     }
      //   }, 3000);
      // }
    },

    showLoading: function() {
      if (this.loadingState) {
        this.loadingState.style.display = 'flex';
      }
      if (this.itemsContainer) {
        this.itemsContainer.style.display = 'none';
      }
      if (this.recommendationsVisible) {
        this.toggleRecommendations(false);
      }
    },

    hideLoading: function() {
      if (this.loadingState) {
        this.loadingState.style.display = 'none';
      }
      if (this.itemsContainer) {
        this.itemsContainer.style.display = 'block';
      }
    },

    showError: function(message) {
      // 隐藏空购物车状态，避免错误信息和空车图标同时显示
      if (this.emptyState) {
        this.emptyState.style.display = 'none';
      }
      if (this.footer) {
        this.footer.style.display = 'none';
      }
      if (this.itemsContainer) {
        this.itemsContainer.style.display = 'block';
        this.itemsContainer.innerHTML = `<div style="padding: 20px; text-align: center; color: #d32f2f;">${this.escapeHtml(message)}<br><button type="button" onclick="CartDrawer.loadCart()" style="margin-top:12px;padding:8px 16px;cursor:pointer;border:1px solid #d32f2f;background:transparent;color:#d32f2f;border-radius:4px;">重试</button></div>`;
      }
    },

    toggleRecommendations: function(shouldShow) {
      if (!this.recommendationsContainer || !this.recommendationsHasContent || !this.body || !this.inner) return;

      const show = Boolean(shouldShow);
      if (show) {
        this.recommendationsContainer.removeAttribute('hidden');
        this.recommendationsContainer.classList.add('is-visible');
        this.body.classList.add('mt-cart-drawer__body--has-recommendations');
        this.inner.classList.add('has-recommendations');
      } else {
        this.recommendationsContainer.setAttribute('hidden', 'hidden');
        this.recommendationsContainer.classList.remove('is-visible');
        this.body.classList.remove('mt-cart-drawer__body--has-recommendations');
        this.inner.classList.remove('has-recommendations');
      }

      this.recommendationsVisible = show;
    },

    formatMoney: function(cents) {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      // Fallback
      return '$' + (cents / 100).toFixed(2);
    },

    escapeHtml: function(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  };

  // Global function to open cart drawer
  window.openCartDrawer = function() {
    CartDrawer.open();
  };

  // Global function to add to cart and open drawer
  window.addToCartAndOpenDrawer = function(variantId, quantity) {
    quantity = quantity || 1;
    
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
      .then(function(response) {
        if (!response.ok) throw new Error('Failed to add to cart');
        return response.json();
      })
      .then(function(item) {
        // Reload cart and open drawer
        CartDrawer.loadCart();
        CartDrawer.open();
        
        // Dispatch event
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function(error) {
        console.error('Error adding to cart:', error);
        alert('Failed to add item to cart. Please try again.');
      });
  };

  // Initialize all cart drawer instances
  function initAllCartDrawers() {
    // 查找所有购物车组件实例
    const cartDrawers = document.querySelectorAll('.mt-cart-drawer');
    
    if (cartDrawers.length === 0) return;
    
    // 如果只有一个实例，使用原有的方式
    if (cartDrawers.length === 1) {
      CartDrawer.init();
    } else {
      // 如果有多个实例，初始化第一个（通常只有一个）
      CartDrawer.init();
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAllCartDrawers();
    });
  } else {
    initAllCartDrawers();
  }
  
  // 支持 Shopify 主题编辑器中的动态加载
  if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail && event.detail.sectionId && 
          document.querySelector(`[data-cart-drawer-id="${event.detail.sectionId}"]`)) {
        // 重新初始化购物车组件
        setTimeout(function() {
          CartDrawer.drawer = null;
          initAllCartDrawers();
        }, 100);
      }
    });
  }

  // Export for external use
  window.CartDrawer = CartDrawer;
})();
